import { useEffect } from 'react';

const SITE_ORIGIN = 'https://streamko.pro';

type SeoLocale = 'en' | 'uk' | 'de';

export interface SeoProps {
  title: string;
  description: string;
  /** Canonical path for this page in the active locale (e.g. "/" or "/uk"). */
  path: string;
  locale: SeoLocale;
  image?: string;
  /** Reciprocal hreflang alternates. */
  alternates?: { locale: SeoLocale; path: string }[];
}

/**
 * Per-page document metadata. Uses React 19's native hoisting of <title>/<meta>/
 * <link> (no react-helmet needed), which also serializes correctly during
 * static prerender. Emits title, description, Open Graph, Twitter card,
 * canonical, and hreflang alternates.
 */
export function Seo({
  title,
  description,
  path,
  locale,
  image = '/images/landing/og.png',
  alternates = [],
}: SeoProps) {
  const origin = typeof window !== 'undefined' ? window.location.origin : SITE_ORIGIN;
  const url = `${origin}${path}`;

  useEffect(() => {
    document.documentElement.lang = locale;
  }, [locale]);

  return (
    <>
      <title>{title}</title>
      <meta name="description" content={description} />
      <link rel="canonical" href={url} />
      <meta property="og:type" content="website" />
      <meta property="og:title" content={title} />
      <meta property="og:description" content={description} />
      <meta property="og:url" content={url} />
      <meta property="og:image" content={`${origin}${image}`} />
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={title} />
      <meta name="twitter:description" content={description} />
      {alternates.map((a) => (
        <link key={a.locale} rel="alternate" hrefLang={a.locale} href={`${origin}${a.path}`} />
      ))}
    </>
  );
}
